import { useState } from 'react';

import styled from '@emotion/styled';

import StatusCell from '../../../components/Badge/StatusCell';
import { PrimaryWhiteButton } from '../../../components/Button/Buttons';
import TableSliderContentWrapper from '../../../components/Table/TableAccessories/TableSliderContentWrapper';
import useRunReconciliation from '../../../hooks/dashboard/reconciliation/useRunReconciliation';
import useTransactionDetail from '../../../hooks/dashboard/transactions/useTransactionDetail';
import { capitalize, formatNaira, tableFormatDateTimeNG } from '../../../utils/utils';
import DisputeRecord from '../Schedules/DisputeRecord';
import ReceiptsSection from './ReceiptsSection';

interface TransactionDetailSliderProps {
  data: Record<string, any>;
  // Called after a manual reconcile so the list behind the slider can refresh.
  onReconciled?: () => void;
}

const RECONCILABLE_STATUSES = ['UNKNOWN', 'PROCESSING'];
// extra_data.fee_kind (backend app/service/pricing.py). Electricity's fees
// come out of what was paid, so its amount is the electricity value.
const FEE_LABELS: Record<string, string> = {
  card_processing: 'Card processing fee',
  transfer_processing: 'Bank transfer fee',
  service: 'Service fee',
};

const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <DetailRow>
    <DetailLabel>{label}</DetailLabel>
    <DetailValue>{children}</DetailValue>
  </DetailRow>
);

const TransactionDetailSlider: React.FC<TransactionDetailSliderProps> = ({
  data,
  onReconciled,
}) => (
  <TableSliderContentWrapper label='Transaction Details'>
    <TransactionDetailBody reference={data?.reference} onReconciled={onReconciled} />
  </TableSliderContentWrapper>
);

export default TransactionDetailSlider;

// The slider's content without its frame, so other sliders (e.g. a risk
// alert) can embed it.
export const TransactionDetailBody: React.FC<{
  reference?: string;
  onReconciled?: () => void;
}> = ({ reference, onReconciled }) => {
  const { detail, loading, refetch } = useTransactionDetail(reference);
  const { loading: reconciling, runOne } = useRunReconciliation();
  const [showDispute, setShowDispute] = useState(false);
  const isScheduled =
    typeof detail?.extra_data?.schedule_code === 'string' ||
    detail?.extra_data?.schedule_pay_now_run_id != null;
  const feeInside = ['card_processing', 'transfer_processing'].includes(String(detail?.extra_data?.fee_kind));

  return (
    <>
      {loading || !detail ? (
        <LoadingText>Loading…</LoadingText>
      ) : (
        <>
          <Section>
            <ReferenceText>{detail.reference}</ReferenceText>
            <StatusCell data={detail} statusKey='status' hasDot positiveType />
            {RECONCILABLE_STATUSES.includes(detail.status) && (
              <ReconcileBox>
                <span>
                  Outcome not confirmed yet. Re-check Paystack and VTpass now instead of
                  waiting for the next automatic run. This never charges the customer.
                </span>
                <PrimaryWhiteButton
                  type='button'
                  disabled={reconciling}
                  onClick={() =>
                    runOne(detail.reference, () => {
                      refetch();
                      onReconciled?.();
                    })
                  }
                >
                  {reconciling ? 'Checking…' : 'Reconcile now'}
                </PrimaryWhiteButton>
              </ReconcileBox>
            )}
          </Section>

          <Section>
            <SectionTitle>Transaction</SectionTitle>
            <Row label='Customer'>{detail.chat_id}</Row>
            <Row label='Service'>{capitalize(detail.type.replace('_', ' '))}</Row>
            <Row label='Provider'>{detail.provider}</Row>
            {/* An electricity fee since the pricing change comes out of what was paid (card or bank transfer). */}
            <Row label={feeInside ? 'Electricity value' : 'Amount'}>
              {formatNaira(Number(detail.amount))}
            </Row>
            {Number(detail.service_fee) > 0 && (
              <>
                <Row label={FEE_LABELS[String(detail.extra_data?.fee_kind)] ?? 'Service fee'}>
                  {formatNaira(Number(detail.service_fee))}
                </Row>
                <Row label={detail.extra_data?.fee_kind === 'transfer_processing' ? 'Transferred' : 'Card charged'}>
                  {formatNaira(Number(detail.amount) + Number(detail.service_fee))}
                </Row>
              </>
            )}
            <Row label='Currency'>{detail.currency}</Row>
            {typeof detail.extra_data?.schedule_code === 'string' && (
              <Row label='Schedule'>{detail.extra_data.schedule_code}</Row>
            )}
            {detail.extra_data?.schedule_pay_now_run_id != null && (
              <Row label='Source'>Pay now after a failed scheduled run</Row>
            )}
            <Row label='Date'>{tableFormatDateTimeNG(detail.created_at)}</Row>
            <Row label='Last updated'>{tableFormatDateTimeNG(detail.updated_at)}</Row>
          </Section>

          <Section>
            <SectionTitle>Provider &amp; Fees</SectionTitle>
            <Row label='Provider reference'>{detail.provider_reference ?? 'N/A'}</Row>
            <Row label='Paystack reference'>{detail.paystack_reference ?? 'N/A'}</Row>
            <Row label='Paystack fee'>
              {detail.paystack_fee ? formatNaira(Number(detail.paystack_fee)) : 'N/A'}
            </Row>
            <Row label='Provider cost'>
              {detail.vtpass_cost ? formatNaira(Number(detail.vtpass_cost)) : 'N/A'}
            </Row>
            <Row label='Profit/Loss'>
              {detail.profit_loss ? formatNaira(Number(detail.profit_loss)) : 'N/A'}
            </Row>
          </Section>

          {isScheduled &&
            (showDispute ? (
              <DisputeRecord reference={detail.reference} />
            ) : (
              <Section>
                <SectionTitle>Scheduled payment</SectionTitle>
                <span>
                  <PrimaryWhiteButton type='button' onClick={() => setShowDispute(true)}>
                    Dispute record
                  </PrimaryWhiteButton>
                </span>
              </Section>
            ))}

          <ReceiptsSection transactionId={detail.id} />

          {detail.ejor && (
            <Section>
              <SectionTitle>Ejor</SectionTitle>
              <Row label='Code'>{detail.ejor.code}</Row>
              <Row label='Status'>{capitalize(detail.ejor.status.replace('_', ' '))}</Row>
              <Row label='Asked by'>
                {detail.ejor.initiator_name
                  ? `${detail.ejor.initiator_name} (${detail.ejor.initiator_chat_id})`
                  : detail.ejor.initiator_chat_id}
              </Row>
              <Row label='Paid by'>
                {detail.ejor.payer_name
                  ? `${detail.ejor.payer_name} (${detail.ejor.payer_chat_id})`
                  : detail.ejor.payer_chat_id ?? 'N/A'}
              </Row>
              <Row label='Payment attempts'>{detail.ejor.payment_attempts}</Row>
              {detail.ejor.attempts.map((attempt) => (
                <AuditRow key={attempt.reference}>
                  <span>{attempt.reference}</span>
                  <span>
                    {attempt.status}
                    {attempt.decline_category ? ` · ${attempt.decline_category}` : ''}
                  </span>
                  <span>{tableFormatDateTimeNG(attempt.created_at)}</span>
                </AuditRow>
              ))}
            </Section>
          )}

          {detail.failure_reason && (
            <Section>
              <SectionTitle>Failure Reason</SectionTitle>
              <FailureText>{detail.failure_reason}</FailureText>
            </Section>
          )}

          {detail.audit_trail.length > 0 && (
            <Section>
              <SectionTitle>Audit Trail</SectionTitle>
              {detail.audit_trail.map((audit, index) => (
                <AuditRow key={index}>
                  <span>{audit.event_type}</span>
                  <span>
                    {audit.previous_status ?? '—'} → {audit.new_status ?? '—'}
                  </span>
                  <span>{tableFormatDateTimeNG(audit.created_at)}</span>
                </AuditRow>
              ))}
            </Section>
          )}

          {detail.ledger_entries.length > 0 && (
            <Section>
              <SectionTitle>Ledger Entries</SectionTitle>
              {detail.ledger_entries.map((entry) => (
                <AuditRow key={entry.id}>
                  <span>{entry.account_type}</span>
                  <span>{entry.direction}</span>
                  <span>{formatNaira(Number(entry.amount))}</span>
                </AuditRow>
              ))}
            </Section>
          )}
        </>
      )}
    </>
  );
};

const ReconcileBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px;
  border-radius: 8px;
  background-color: rgba(202, 138, 4, 0.08);
  border: 1px solid rgba(202, 138, 4, 0.3);
  font-size: 13px;
  color: #3F4A42;

  & > button {
    width: fit-content;
  }
`;

const LoadingText = styled.div`
  color: #5D665F;
  font-size: 14px;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-bottom: 20px;
  border-bottom: 1px solid #E2DED3;

  &:last-of-type {
    border-bottom: none;
  }
`;

const SectionTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #5D665F;
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

const ReferenceText = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #0E1A13;
  word-break: break-all;
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  font-size: 14px;
`;

const DetailLabel = styled.span`
  color: #5D665F;
`;

const DetailValue = styled.span`
  color: #0E1A13;
  font-weight: 500;
  text-align: right;
  word-break: break-all;
`;

const FailureText = styled.div`
  font-size: 14px;
  color: #D92D20;
`;

const AuditRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 8px;
  font-size: 12px;
  color: #3F4A42;
`;
